import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { format, isSameDay } from 'date-fns';
import { ko } from 'date-fns/locale';

import {
  calendarHeader,
  calendarPanel,
  calendarWeekDay,
  calendarWeekHeader,
  datePickerContainer,
  datePickerContent,
} from './DatePicker.css';
import { DatePickerCalendar, DatePickerCalendarRef } from './DatePickerCalendar';
import { SingleDatePickerProps } from './types';
import { createDateBlurHandler, createDateInputHandler, createDateKeyDownHandler } from './utils';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { Input } from '../Input';

const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

const formatDate = (date: Date | undefined) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd', { locale: ko });
};

export const SingleDatePickerPanel = forwardRef<
  HTMLDivElement,
  Omit<SingleDatePickerProps, 'mode'>
>(({ value, onChange, minDate, maxDate, className }, ref) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
  const [inputValue, setInputValue] = useState<string>(value ? formatDate(value) : '');
  const isFocusedRef = useRef<boolean>(false);
  const placeholder = formatDate(new Date());
  const calendarRef = useRef<DatePickerCalendarRef | null>(null);
  const initialMonth = useMemo(() => value || new Date(), [value]);

  const handleSelect = (date: Date | undefined) => {
    if (date instanceof Date) {
      setSelectedDate(date);
      onChange?.(date);
      calendarRef.current?.resetMonths(date);
      calendarRef.current?.scrollToMonth(date);
    }
  };

  // Input의 handleChange
  const handleInputChange = createDateInputHandler(setInputValue, handleSelect);

  // Input의 handleKeyDown
  const handleInputKeyDown = createDateKeyDownHandler(inputValue, setInputValue);

  // Input의 handleBlur
  const handleInputBlur = () => {
    isFocusedRef.current = false;
    const blurHandler = createDateBlurHandler({
      inputValue,
      onReset: () => setInputValue(formatDate(selectedDate)),
      setDate: (date) => {
        if (selectedDate && isSameDay(date, selectedDate)) {
          setInputValue(formatDate(selectedDate));
          return;
        }
        handleSelect(date);
      },
      validationOptions: {
        minDate,
        maxDate,
      },
    });
    blurHandler();
  };

  // Input에 포커스가 갔을 때 현재 값을 입력값으로 설정
  const handleInputFocus = () => {
    isFocusedRef.current = true;
    setInputValue(formatDate(selectedDate));
  };

  // selectedDate가 외부에서 변경될 때 inputValue 동기화 (포커스가 없을 때만)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(formatDate(selectedDate));
    }
  }, [selectedDate]);

  return (
    <div ref={ref} className={clsx(datePickerContainer, className)}>
      <div className={datePickerContent({ mode: 'single' })}>
        <div className={calendarPanel}>
          <div className={calendarHeader}>
            <Flex align="center" gap={8}>
              <Input
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                inputSize="m"
              />
              <Button buttonType="border" onClick={() => handleSelect(new Date())}>
                오늘
              </Button>
            </Flex>

            <div className={calendarWeekHeader}>
              {WEEKDAYS.map((day) => (
                <span key={day} className={calendarWeekDay}>
                  {day}
                </span>
              ))}
            </div>
          </div>

          <DatePickerCalendar
            ref={calendarRef}
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            minDate={minDate}
            maxDate={maxDate}
            initialMonth={initialMonth}
          />
        </div>
      </div>
    </div>
  );
});

SingleDatePickerPanel.displayName = 'SingleDatePickerPanel';
